import styled from "@emotion/styled";
import { Spacer } from "components/general/Spacer/Spacer";
import { Text } from "components/general/Text";
import { GUILD_ROLES_EMOJIS } from "config/consts";
import { FlexContainer } from "containers/FlexContainer";
import { DeviceType, useDeviceType } from "hooks/useDeviceType";
import { useSelectedGuildRole } from "hooks/useSelectedGuildRole";
import React, { PropsWithChildren, useMemo } from "react";
import { withIsVisible } from "utils/reactUtils";
import { getAddressLabel } from "utils/userUtils";
import { GlobalStore } from "../../../store/global/GlobalStore";
import { NavbarProfilePicture } from "./NavbarProfilePicture";
import { ProfileActions } from "./ProfileActions/ProfileActions";
import { useDeviceBasedTextStyles } from "hooks/useDeviceBasedTextStyles";
import { Address } from "viem";
import { ConnectButton } from "@rainbow-me/rainbowkit";

interface ProfileProps {
  isDisplayed: boolean;
  elementToClickRef: React.Ref<HTMLDivElement>;
  elementToDisplayRef: React.Ref<HTMLDivElement>;
  closeDisplay: () => void;
  onClick: () => void;
}

export const Profile = ({
  isDisplayed,
  elementToClickRef,
  elementToDisplayRef,
  closeDisplay,
  onClick,
}: ProfileProps) => {
  const isTablet = useDeviceType() === DeviceType.Tablet;
  const address = GlobalStore.useState(s => s.address);
  const ensName = GlobalStore.useState(s => s.ensName);

  const title = useMemo(() => {
    if (address) {
      return (
        <Text textStyle="Title" size="XS" isVisible={!isTablet}>
          {getAddressLabel(address as Address, ensName)}
        </Text>
      );
    }
  }, [address, ensName, isTablet]);

  return (
    <Container ref={elementToClickRef}>
      <PfpAndGuildRoleContainer onClick={onClick}>
        <NavbarProfilePicture />
        {title}
        <GuildRole isVisible={address !== "" && !isTablet} />
      </PfpAndGuildRoleContainer>
      <ProfileActions
        isVisible={isDisplayed && address !== ""}
        ref={elementToDisplayRef as React.Ref<HTMLDivElement> | undefined}
        closeDisplay={closeDisplay}
      />
    </Container>
  );
};

// Sub-components

interface PfpAndGuildRoleContainerProps {
  onClick?: () => void;
}

const PfpAndGuildRoleContainer: React.FC<PropsWithChildren<PfpAndGuildRoleContainerProps>> = ({
  children,
  onClick,
}) => {
  return (
    <ConnectButton.Custom>
      {({ account, chain, openConnectModal, mounted }) => {
        const connected = mounted && account && chain;
        return (
          <SubContainer onClick={!connected ? openConnectModal : onClick}>{children}</SubContainer>
        ); // Directly return children if connected
      }}
    </ConnectButton.Custom>
  );
};

const GuildRole = withIsVisible(() => {
  const selectedRole = useSelectedGuildRole();
  const theme = GlobalStore.useState(s => s.theme);
  const RoleIcon = GUILD_ROLES_EMOJIS[selectedRole?.id];
  return (
    <GuildRoleContainer isHidden={selectedRole == null}>
      <GuildRoleText>{selectedRole?.name}</GuildRoleText>
      <Spacer width="2px" />
      {RoleIcon && <RoleIcon width={18} height={18} color={theme.font.secondary.default} />}
    </GuildRoleContainer>
  );
});

const Container = styled.div(() => {
  return {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    position: "relative",
  };
});

const SubContainer = styled.div`
  font-size: 14px;
  display: flex;
  flex-direction: column;
  cursor: pointer;
`;

const GuildRoleContainer = styled(FlexContainer)<{ isHidden: boolean }>(({ isHidden }) => {
  return {
    paddingTop: "4px",
    height: 22,
  };
});

const GuildRoleText = styled.div(({ theme }) => {
  const textStyles = useDeviceBasedTextStyles({ shouldScale: true });
  return {
    color: theme.font.secondary.default,
    font: textStyles.Title.XS,
  };
});
